import React, { useEffect, useState } from 'react'
import { usePartnerAuth } from '../../context/PartnerAuthContext'
import { supabase } from '../../lib/supabaseClient'

// ── Types ──────────────────────────────────────────────

type PartnerMember = {
  id: string
  email: string
  full_name: string | null
  partner_role: 'ADMIN_PARTNER' | 'USER_PARTNER'
  is_active_partner: boolean
  created_at: string
}

type CreateMemberForm = {
  email: string
  password: string
  full_name: string
  partner_role: 'ADMIN_PARTNER' | 'USER_PARTNER'
}

const EMPTY_CREATE_FORM: CreateMemberForm = {
  email: '',
  password: '',
  full_name: '',
  partner_role: 'USER_PARTNER',
}

// ── Helpers ────────────────────────────────────────────

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })

// ── Styles ─────────────────────────────────────────────

const styles = {
  card: {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '0.75rem',
    padding: '2rem',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.9rem',
  } as React.CSSProperties,
  th: {
    textAlign: 'left' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '2px solid #e2e8f0',
    color: '#64748b',
    fontWeight: '600',
    fontSize: '0.85rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  } as React.CSSProperties,
  td: {
    padding: '0.75rem 0.5rem',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155',
  } as React.CSSProperties,
  btnPrimary: {
    padding: '0.5rem 1rem',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  btnSecondary: {
    padding: '0.4rem 0.75rem',
    background: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  btnDanger: {
    padding: '0.4rem 0.75rem',
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  btnSuccess: {
    padding: '0.4rem 0.75rem',
    background: '#f0fdf4',
    color: '#16a34a',
    border: '1px solid #bbf7d0',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  badge: (active: boolean): React.CSSProperties => ({
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '9999px',
    fontSize: '0.8rem',
    fontWeight: '500',
    background: active ? '#dcfce7' : '#fee2e2',
    color: active ? '#166534' : '#991b1b',
  }),
  roleBadge: (role: string): React.CSSProperties => ({
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '9999px',
    fontSize: '0.8rem',
    fontWeight: '500',
    background: role === 'ADMIN_PARTNER' ? '#dbeafe' : '#f1f5f9',
    color: role === 'ADMIN_PARTNER' ? '#1e40af' : '#475569',
  }),
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  } as React.CSSProperties,
  modal: {
    background: 'white',
    borderRadius: '0.75rem',
    padding: '2rem',
    width: '100%',
    maxWidth: '520px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  } as React.CSSProperties,
  formGroup: {
    marginBottom: '1rem',
  } as React.CSSProperties,
  label: {
    display: 'block',
    marginBottom: '0.3rem',
    fontWeight: '500',
    fontSize: '0.9rem',
    color: '#334155',
  } as React.CSSProperties,
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,
  select: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    background: 'white',
  } as React.CSSProperties,
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '0.75rem 1rem',
    borderRadius: '0.375rem',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  infoBox: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    color: '#1e40af',
    padding: '0.75rem 1rem',
    borderRadius: '0.375rem',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  } as React.CSSProperties,
}

// ── Modal de création de membre (stub) ─────────────────

type CreateMemberModalProps = {
  onClose: () => void
  onSubmit: (form: CreateMemberForm) => Promise<void>
}

const CreateMemberModal: React.FC<CreateMemberModalProps> = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState<CreateMemberForm>({ ...EMPTY_CREATE_FORM })
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const handleChange = (field: keyof CreateMemberForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.email.trim()) { setError('L\'email est obligatoire.'); return }
    if (!form.password || form.password.length < 6) { setError('Le mot de passe doit contenir au moins 6 caractères.'); return }
    if (!form.full_name.trim()) { setError('Le nom complet est obligatoire.'); return }

    setSaving(true)
    try {
      await onSubmit(form)
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de la création.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={styles.overlay} onClick={onClose} data-testid="create-member-modal">
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#1e293b' }}>
          ➕ Nouveau membre partenaire
        </h3>

        <div style={styles.infoBox} data-testid="create-member-info">
          <strong>💡 Info : </strong>
          Le nouveau membre pourra se connecter immédiatement avec ses identifiants.
        </div>

        {error && (
          <div style={styles.errorBox} data-testid="create-member-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email *</label>
            <input
              style={styles.input}
              type="email"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="membre@example.com"
              data-testid="member-email-input"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Mot de passe *</label>
            <input
              style={styles.input}
              type="password"
              value={form.password}
              onChange={(e) => handleChange('password', e.target.value)}
              placeholder="Minimum 6 caractères"
              data-testid="member-password-input"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Nom complet *</label>
            <input
              style={styles.input}
              type="text"
              value={form.full_name}
              onChange={(e) => handleChange('full_name', e.target.value)}
              placeholder="Prénom Nom"
              data-testid="member-fullname-input"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Rôle partenaire</label>
            <select
              style={styles.select}
              value={form.partner_role}
              onChange={(e) => handleChange('partner_role', e.target.value)}
              data-testid="member-role-select"
            >
              <option value="USER_PARTNER">Membre (USER_PARTNER)</option>
              <option value="ADMIN_PARTNER">Administrateur (ADMIN_PARTNER)</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <button type="button" onClick={onClose} style={styles.btnSecondary}>
              Annuler
            </button>
            <button type="submit" style={styles.btnPrimary} disabled={saving} data-testid="member-save-btn">
              {saving ? 'Création...' : 'Créer le membre'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Page principale ────────────────────────────────────

const PartnerMembersPage: React.FC = () => {
  const { partner, partnerProfile, isAdminPartner } = usePartnerAuth()
  const [members, setMembers] = useState<PartnerMember[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)

  // ── Chargement des membres ──

  const loadMembers = async () => {
    if (!partner) return
    setLoading(true)
    setError(null)

    const { data, error: err } = await supabase
      .from('profiles')
      .select('id, email, full_name, partner_role, is_active_partner, created_at')
      .eq('role', 'PARTNER')
      .eq('partner_id', partner.id)
      .order('created_at', { ascending: true })

    if (err) {
      console.error('Erreur chargement membres:', err)
      setError('Impossible de charger les membres.')
    } else {
      setMembers(data ?? [])
    }
    setLoading(false)
  }

  useEffect(() => {
    void loadMembers()
  }, [partner?.id])

  // ── Toggle actif/inactif ──

  const handleToggleActive = async (member: PartnerMember) => {
    if (member.id === partnerProfile?.id) {
      alert('Vous ne pouvez pas vous désactiver vous-même.')
      return
    }

    const { error: err } = await supabase
      .from('profiles')
      .update({ is_active_partner: !member.is_active_partner })
      .eq('id', member.id)
      .eq('partner_id', partner!.id)

    if (err) {
      console.error('Erreur toggle membre:', err)
      alert('Erreur lors de la modification du statut.')
      return
    }
    void loadMembers()
  }

  // ── Création de membre via Edge Function ──

  const handleCreateMember = async (form: CreateMemberForm) => {
    if (!partner) return

    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('Session expirée, veuillez vous reconnecter.')
    }

    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL || ''}/functions/v1/create-partner-member`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          full_name: form.full_name,
          partner_role: form.partner_role,
        }),
      }
    )

    const body = await res.json()

    if (!res.ok) {
      throw new Error(body?.error || `Erreur ${res.status}`)
    }

    setShowCreateModal(false)
    void loadMembers()
  }

  // ── Guard: accès ADMIN_PARTNER uniquement ──

  if (!isAdminPartner) {
    return (
      <div style={styles.card} data-testid="members-access-denied">
        <h2 style={{ color: '#991b1b' }}>Accès refusé</h2>
        <p>Seuls les administrateurs partenaires peuvent gérer les membres.</p>
      </div>
    )
  }

  // ── Rendu ──

  return (
    <div style={styles.card} data-testid="members-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#1e293b' }}>
          👥 Gestion des membres
        </h2>
        <button
          style={styles.btnPrimary}
          onClick={() => setShowCreateModal(true)}
          data-testid="create-member-btn"
        >
          + Nouveau membre
        </button>
      </div>

      {/* Erreur */}
      {error && <div style={styles.errorBox}>{error}</div>}

      {/* Chargement */}
      {loading ? (
        <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>Chargement des membres...</p>
      ) : members.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#64748b',
            background: '#f8fafc',
            borderRadius: '0.5rem',
          }}
          data-testid="no-members"
        >
          <p style={{ fontSize: '1.1rem', margin: '0 0 0.5rem 0' }}>Aucun membre pour le moment.</p>
          <p style={{ margin: 0 }}>Cliquez sur « + Nouveau membre » pour en ajouter un.</p>
        </div>
      ) : (
        /* Table des membres */
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table} data-testid="members-table">
            <thead>
              <tr>
                <th style={styles.th}>Nom</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Rôle</th>
                <th style={styles.th}>Statut</th>
                <th style={styles.th}>Inscrit le</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} data-testid={`member-row-${m.email}`}>
                  <td style={{ ...styles.td, fontWeight: '500' }}>
                    {m.full_name || '—'}
                    {m.id === partnerProfile?.id && (
                      <span style={{ fontSize: '0.75rem', color: '#3b82f6', marginLeft: '0.5rem' }}>
                        (vous)
                      </span>
                    )}
                  </td>
                  <td style={styles.td}>{m.email}</td>
                  <td style={styles.td}>
                    <span style={styles.roleBadge(m.partner_role)} data-testid={`member-role-${m.email}`}>
                      {m.partner_role === 'ADMIN_PARTNER' ? 'Admin' : 'Membre'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span
                      style={styles.badge(m.is_active_partner)}
                      data-testid={`member-status-${m.email}`}
                    >
                      {m.is_active_partner ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td style={styles.td}>{formatDate(m.created_at)}</td>
                  <td style={styles.td}>
                    {m.id !== partnerProfile?.id && (
                      <button
                        style={m.is_active_partner ? styles.btnDanger : styles.btnSuccess}
                        onClick={() => handleToggleActive(m)}
                        data-testid={`toggle-member-${m.email}`}
                        title={m.is_active_partner ? 'Désactiver' : 'Activer'}
                      >
                        {m.is_active_partner ? '🔒 Désactiver' : '🔓 Activer'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal création */}
      {showCreateModal && (
        <CreateMemberModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateMember}
        />
      )}
    </div>
  )
}

export default PartnerMembersPage
